import { DOCUMENT } from '@angular/common';
import { AfterViewChecked, Component, Inject, Injectable, OnInit, ViewChild } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import { UploadFileData } from '../models/uploads';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Global } from '../shared/global.constants';
import { SelectEvent, SuccessEvent, UploadEvent } from '@progress/kendo-angular-upload';
import {
  HttpErrorResponse, HttpEvent, HttpEventType,
  HttpHandler, HttpInterceptor, HttpProgressEvent, HttpRequest, HttpResponse
} from '@angular/common/http';
import { concat, Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { UploadService } from '../shared/services/uploads/upload.service';
import { AuthService } from '../shared/services/auth/auth.service';
import { DisplayNotificationService } from '../shared/services/common/notification.service';
import { ReadConfigService } from '../shared/services/common/read-config.service';
import { Utility } from '../shared/utility';
import { DataBindingDirective } from '@progress/kendo-angular-grid';

@Component({
  selector: 'app-uploads',
  templateUrl: './uploads.component.html',
  styleUrls: ['./uploads.component.css']
})

export class UploadsComponent implements OnInit, AfterViewChecked {

  fileRestrictions: any;
  disclaimer: any;
  maxTenEmails: any;
  mobileText: any;
  recordCountWarningMsg: any;
  amountWarningMsg: any;
  gridView: any;
  uploadTypes: string[];
  msraEmailList: any;
  locations: string[];
  config: any;
  filterRows: any;
  disableButton = true;
  isValidType = false;
  currentType = '';
  RcdAndAmt = false;
  currentUpdate = false;
  prevUpdate = false;
  fileNameErrorMsg = '';
  pped: Date = new Date();
  minDate: Date = new Date(this.pped.getFullYear(), 0, 1);
  maxDate: Date = new Date(this.pped.getFullYear(), 11, 1);
  msg = '';
  allUploadIds: any = [];
  decimals = 2;
  location = '';
  uploadSaveUrl = '';
  readonly uploadRemoveUrl: string = 'removeUrl';
  gridState: State = {
    sort: [],
    take: 5,
    skip: 0
  };
  apiUrl = this.readConfigService.getApiUrl();
  MaxRecordCount = this.readConfigService.getMaxRecordCount();
  MaxAmount = this.readConfigService.getMaxAmount();
  @ViewChild(DataBindingDirective, { static: true }) directive;
  showOld = false;
  editForm: UntypedFormGroup = new UntypedFormGroup({
    fileType: new UntypedFormControl(''),
    location: new UntypedFormControl(''),
    pped: new UntypedFormControl(''),
    emailList: new UntypedFormControl(''),
    recordCount: new UntypedFormControl('', [Validators.max(this.MaxRecordCount)]),
    totalAmount: new UntypedFormControl('', [Validators.max(this.MaxAmount)]),
    notes: new UntypedFormControl('')
  });

  constructor(
    @Inject(DOCUMENT) private document: any,
    private uploadService: UploadService,
    private authService: AuthService,
    private notificationService: DisplayNotificationService,
    private readConfigService: ReadConfigService) { }

  ngOnInit() {
    if (this.authService.isLoggedIn) {
      this.authService.loggedInChecker(this.authService.isLoggedIn);
    }
    this.notificationService.setUpLoading(true);
    setTimeout(() => {
      this.showOld = this.authService.getDarkKeyValue('Uploads');
      if (this.showOld) {
        this.config = this.readConfigService.getUploadFile();
        this.disclaimer = this.config.Disclaimer;
        this.maxTenEmails = this.config.MaxTenEmails;
        this.mobileText = this.config.MobileText;
        this.recordCountWarningMsg = this.config.RecordCountWarning;
        this.amountWarningMsg = this.config.AmountWarning;
        this.fileRestrictions = this.config.UploadFileRestrictions;
        this.uploadSaveUrl = this.apiUrl + Global.BASE_PM_UPLOAD_FROMFILE_ENDPOINT;
        this.loadData();
        setTimeout(() => this.setPlaceHolders(), 200);
        this.editForm.controls.emailList.valueChanges.subscribe(() => {
          this.disableButton = false;
          if (this.editForm.controls.emailList.invalid) {
            this.disableButton = true;
          }
        });
      }
      this.notificationService.setUpLoading(false);
    }, 2000);
  }

  ngAfterViewChecked(): void {
    if (this.showOld) {
      if (this.editForm.dirty) {
        this.currentUpdate = false;
      } else if (!(this.editForm.dirty)) {
        this.currentUpdate = true;
      }
      if (this.currentUpdate !== this.prevUpdate) {
        this.authService.formChanged(this.currentUpdate);
        this.prevUpdate = this.currentUpdate;
      }
      const width = (window.innerWidth > 0) ? window.innerWidth : screen.width;
      const fileUploadView = document.getElementById('file-upload-view');
      const mobileViewMsg = document.getElementById('profile-mobile-view-msg');
      const uploadHistoryView = document.getElementById('upload-history-view');
      if (width < 993) {
        fileUploadView.style.display = 'none';
        mobileViewMsg.style.display = 'inline-block';
        uploadHistoryView.style.width = 'auto';
      } else {
        fileUploadView.style.display = 'inline-block';
        mobileViewMsg.style.display = 'none';
        uploadHistoryView.style.width = 'inherit';
      }
    }
  }

  setModel(pped) {
    this.pped = pped;
  }

  loadData() {
    this.notificationService.setUpLoading(true);
    this.loadUploadHistory();
    this.loadUploadTypes();
    this.loadLocations();
  }

  valueChange(args: any): void {
    if (args !== undefined) {
      if (this.uploadTypes.indexOf(args) !== -1) {
        this.isValidType = true;
      }
    } else {
      this.isValidType = false;
      this.reset();
    }
    if (this.currentType !== args) {
      this.reset();
    }
    this.currentType = args;
    this.RcdAndAmt = (this.currentType === 'Wages' || this.currentType === 'Deductions');
    if (this.currentType === 'Other') {
      this.editForm.controls.emailList.validator = Validators.required;
    } else {
      this.disableButton = false;
      this.editForm.controls.emailList.clearValidators();
    }
  }

  selectEventHandler(e: SelectEvent) {
    const fileName = (e.files[0].name).substr(0, e.files[0].name.lastIndexOf('.'));
    if (fileName.length > 200) {
      this.fileNameErrorMsg = this.config.MaxFileNameErrorMsg;
      this.displayNotification({ success: false, message: this.fileNameErrorMsg });
      e.preventDefault();
    }
  }

  uploadComplete(obj: any, upload: any) {
    this.loadUploadHistory();
    if (this.allUploadIds !== undefined && this.allUploadIds.length > 0) {
      for (const allUploadId of this.allUploadIds) {
        upload.removeFilesByUid(allUploadId);
      }
      this.allUploadIds = [];
    }
  }

  loadUploadHistory(): void {
    this.uploadService.getHistory().subscribe(data => {
      if (data && data.success) {
        if (data.result.length > 0) {
          data.result.forEach(el => {
            el.created = Utility.removeTimeFromDate(el.created);
          });
          this.gridView = data.result;
        } else {
          this.gridView = [];
        }
      } else {
        this.notificationService.setErrorNotification();
      }
      this.notificationService.setUpLoading(false);
    },
      (err: HttpErrorResponse) => {
        console.log(err);
        this.notificationService.setUpLoading(false);
        this.notificationService.setErrorNotification();
      });
  }

  loadLocations(): void {
    this.uploadService.getLocations().subscribe(data => {
      this.locations = data;
      this.location = data[0];
    },
      (err: HttpErrorResponse) => {
        console.log(err);
        this.notificationService.setUpLoading(false);
        this.notificationService.setErrorNotification();
      });
  }

  loadUploadTypes() {
    this.uploadService.getFileCategories().subscribe(data => {
      if (data && data.success) {
        this.uploadTypes = data.categoriesList;
        this.msraEmailList = data.emailList.split(',');
      } else {
        this.notificationService.setErrorNotification();
      }
    },
      (err: HttpErrorResponse) => {
        console.log(err);
        this.notificationService.setUpLoading(false);
        this.notificationService.setErrorNotification();
      });
  }

  uploadEventHandler(e: UploadEvent, ufile: any) {
    this.notificationService.setUpLoading(true);
    ufile.fileType = this.currentType;
    if (ufile.fileType === 'Other' && this.isNullOrUndefined(ufile.emailList) === '') {
      e.preventDefault();
      return;
    }
    const files = e.files;
    for (const file of files) {
      const a = file.uid;
      if (a !== undefined) {
        this.allUploadIds.push(a);
      }
    }
    ufile.pped = new Date(ufile.pped);
    const data = new UploadFileData();
    data.fileType = ufile.fileType;
    let dt = new Date();
    if (this.isNullOrUndefined(this.pped) !== '') {
      dt = new Date(this.pped);
    }
    data.pped = dt.toDateString();
    data.emailList = this.isNullOrUndefined(ufile.emailList);
    data.location = this.isNullOrUndefined(this.location) === '' ? this.locations[0] : this.location;
    data.notes = this.isNullOrUndefined(ufile.notes);
    data.recordCount = this.isNullOrUndefined(ufile.recordCount) === '' ? 0 : parseInt(ufile.recordCount, 0);
    data.totalAmount = this.isNullOrUndefined(ufile.totalAmount) === '' ? 0.00 : parseFloat(ufile.totalAmount.toFixed(2));
    if (this.authService !== null && this.authService.currentUser !== null) {
      data.userId = this.authService.currentUser.Id.toString();
    }
    data.userInfo = JSON.stringify(this.uploadService.UserInfo());
    e.data = data;
  }

  errorEventHandler(e: ErrorEvent) {
    this.msg = this.config.ErrorMessage;
    this.displayNotification({ success: false, message: this.msg });
    this.notificationService.setUpLoading(false);
  }

  displayNotification(response) {
    this.notificationService.setUpNotification(response);
  }

  uploadSuccess(e: SuccessEvent) {
    const response = e.response;
    if (response && response.body) {
      this.displayNotification(response.body);
    }
    this.notificationService.setUpLoading(false);
    this.reset();
  }

  reset() {
    this.editForm.reset();
    this.location = this.locations[0];
    this.pped = new Date();
  }

  locationChange(args: any) {
    if (args !== this.location) {
      this.location = args;
    }
  }

  isNullOrUndefined(args: any) {
    if (args === undefined || args === null) {
      return '';
    }
    return args;
  }

  showTooltip(e: any): void {
    const target = e.target;
    const matches = target.matches ? target.matches('.k-grid-content td') : target.msMatchesSelector('.k-grid-content td');
    if (target.nodeName === 'TD' && matches) {
      target.classList.toggle('tooLong', e.target.offsetWidth < e.target.scrollWidth);
    }
  }

  setPlaceHolders() {
    const filterRow = this.document.getElementsByClassName('k-filter-row');
    if (filterRow !== null && filterRow[0]) {
      const inputRows = filterRow[0].getElementsByTagName('input');
      if (inputRows) {
        this.filterRows = inputRows;
      }
    }
  }
}

@Injectable()
export class UploadInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url === 'saveUrl') {
      const events: Observable<HttpEvent<any>>[] = [0, 30, 60, 100].map((x) => of({
        type: HttpEventType.UploadProgress,
        loaded: x,
        total: 100
      } as HttpProgressEvent).pipe(delay(1000)));
      const success = of(new HttpResponse({ status: 200 })).pipe(delay(1000));
      events.push(success);

      return concat(...events);
    }

    if (req.url === 'removeUrl') {
      return of(new HttpResponse({ status: 200 }));
    }

    return next.handle(req);
  }
}
